import { useCreateVenue, usePromoteVenue } from "@/hooks/use-venue-management";
import { X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

type VenueCreateDialogProps = {
  open: boolean;
  onClose: () => void;
  initialName?: string;
  isPromote?: boolean;
};

/** venue 作成・昇格モーダル */
export const VenueCreateDialog = ({
  open,
  onClose,
  initialName = "",
  isPromote = false,
}: VenueCreateDialogProps) => {
  const [name, setName] = useState(initialName);
  const [strategy, setStrategy] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);
  const strategyInputRef = useRef<HTMLTextAreaElement>(null);
  const createVenue = useCreateVenue();
  const promoteVenue = usePromoteVenue();
  const mutation = isPromote ? promoteVenue : createVenue;

  useEffect(() => {
    if (open) {
      setName(initialName);
      setStrategy("");
      if (initialName) {
        setTimeout(() => strategyInputRef.current?.focus(), 100);
      } else {
        setTimeout(() => nameInputRef.current?.focus(), 100);
      }
    }
  }, [open, initialName]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose],
  );

  useEffect(() => {
    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [open, handleKeyDown]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    if (!trimmedName) return;

    mutation.mutate(
      {
        name: trimmedName,
        strategy: strategy.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
        onKeyDown={() => {}}
        role="presentation"
      />
      <div className="relative mx-4 w-full max-w-sm rounded-lg bg-background p-6 shadow-lg">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-bold text-foreground">
            {isPromote ? "お店を昇格" : "お店を追加"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1 text-muted-foreground hover:bg-muted"
            aria-label="閉じる"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={handleSubmit}>
          <input
            ref={nameInputRef}
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="店名"
            readOnly={isPromote}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring read-only:bg-muted"
          />
          <textarea
            ref={strategyInputRef}
            value={strategy}
            onChange={(e) => setStrategy(e.target.value)}
            placeholder="戦略メモ（AI 補完のヒントになります）"
            rows={3}
            className="mt-3 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
          />
          <div className="mt-4 flex justify-end">
            <button
              type="submit"
              disabled={!name.trim() || mutation.isPending}
              className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
            >
              {mutation.isPending
                ? isPromote
                  ? "昇格中..."
                  : "追加中..."
                : isPromote
                  ? "昇格"
                  : "追加"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
